// client/src/lib/api.js — unified /me normalisation + session-cookie auth + Socket.IO
import { io } from 'socket.io-client';

/** =========================================
 *  Debug toggles
 *  ========================================= */
export const DEBUG_API = false;
export const DEBUG_SOCKET = false;

/** Env helpers (supports Vite + CRA) */
const V = (typeof import.meta !== 'undefined' && import.meta.env) || {};
const get = (viteKey, craKey) => V[viteKey] || process.env[craKey];

/** Safe window/location access */
const hasWindow = typeof window !== 'undefined' && !!window.location;
const WLOC = hasWindow ? window.location : { origin: '', hostname: 'localhost', port: '3000' };

/** Resolve API base URL */
export const API_BASE = (() => {
  // Optional runtime override (useful for debugging in the console)
  try {
    if (hasWindow && window.__CHITCONNECT_API_BASE__) {
      return String(window.__CHITCONNECT_API_BASE__).replace(/\/+$/, '');
    }
  } catch {}

  const explicit =
    get('VITE_API_BASE', 'REACT_APP_API_BASE') ||
    get('VITE_API_BASE_URL', 'REACT_APP_API_BASE_URL');
  if (explicit) return String(explicit).replace(/\/+$/, '');

  const isDev =
    (V.MODE || process.env.NODE_ENV) === 'development' ||
    (hasWindow && (WLOC.port === '3000' || WLOC.hostname === 'localhost'));

  if (isDev) {
    const host = hasWindow ? WLOC.hostname : 'localhost';
    return `http://${host}:5000`;
  }

  // Production: prefer API subdomain(s) so the SPA origin (Amplify/S3/CloudFront)
  // never receives /api/* requests (which would otherwise return index.html).
  if (!hasWindow) return 'http://localhost:5000'.replace(/\/+$/, '');

  const host = String(WLOC.hostname || '').toLowerCase();
  const stage = String(
    get('VITE_STAGE', 'REACT_APP_STAGE') ||
    get('VITE_DEPLOY_ENV', 'REACT_APP_DEPLOY_ENV') ||
    ''
  ).toLowerCase();
  const parts = host.split('.').filter(Boolean);
  const root = parts.slice(-2).join('.');

  // Known deploys
  if (host === 'uat.chitconnect.com') return 'https://uat-api.chitconnect.com';
  if (host === 'www.uat.chitconnect.com') return 'https://uat-api.chitconnect.com';
  if (host === 'chitconnect.com' || host === 'www.chitconnect.com') return 'https://api.chitconnect.com';
  if (host === 'uat-api.chitconnect.com' || host === 'api.chitconnect.com') return String(WLOC.origin || '').replace(/\/+$/, '');

  // Generic mapping for *.chitconnect.com -> api subdomain
  if (host.endsWith('.chitconnect.com') && !host.startsWith('api.') && root === 'chitconnect.com') {
    const label = parts[0] || '';
    if (label === 'uat') return `https://uat-api.${root}`;
    if (label === 'staging') return `https://staging-api.${root}`;
    return `https://api.${root}`;
  }

  // If the SPA is hosted on a CDN (Amplify/CloudFront) and we failed to resolve,
  // never fall back to the SPA origin (it would serve index.html for /api/* and break JSON calls).
  const cdnHost =
    host.endsWith('.amplifyapp.com') ||
    host.endsWith('.cloudfront.net') ||
    host.endsWith('.amazonaws.com');

  if (cdnHost) {
    // Best-effort: use the configured stage if present, otherwise default to UAT.
    if (stage === 'staging') return 'https://staging-api.chitconnect.com';
    if (stage === 'prod' || stage === 'production') return 'https://api.chitconnect.com';
    return 'https://uat-api.chitconnect.com';
  }

  // Last resort (better than same-origin HTML): assume prod API.
  return 'https://api.chitconnect.com';
})();

/** Build absolute API URL from a path */
export const api = (path) =>
  path.startsWith('http')
    ? path
    : `${API_BASE}${path.startsWith('/') ? path : `/${path}`}`;

/** Token shim for legacy UI that expects a "token" (not used in headers)
 *  Persist in localStorage to avoid "logout" after inactivity on mobile.
 *  Fallback to sessionStorage for backwards compatibility.
 */
export const setToken = (v) => {
  try {
    if (v) {
      localStorage.setItem('token', v);
      sessionStorage.setItem('token', v);
    } else {
      localStorage.removeItem('token');
      sessionStorage.removeItem('token');
    }
  } catch {}
};
export const getToken = () => {
  try {
    const t = localStorage.getItem('token') || sessionStorage.getItem('token') || '';
    return t === 'session' ? 'session' : t;
  } catch { return ''; }
};

/** Auth-aware fetch (cookie sessions). If body is a plain object, auto-JSON.
 *  Additionally, if a string body *looks* like JSON and Content-Type is absent,
 *  set `application/json` to help legacy callers that pre-stringify.
 */
export const apiFetch = (path, options = {}) => {
  if (DEBUG_API) console.debug('[apiFetch]', { url: api(path), options });

  const opts = { credentials: 'include', ...options };
  const headers = { ...(opts.headers || {}) };

  // If a JWT token exists (legacy auth), send it as Bearer so token-protected routes (eg /api/users/me) work
  const token = getToken();
  // 'session' is a local marker meaning cookie-based auth; it is NOT a JWT.
  if (token && token !== 'session' && !headers.Authorization && !headers.authorization) {
    headers.Authorization = `Bearer ${token}`;
  }


  const isPlainObject =
    opts.body &&
    typeof opts.body === 'object' &&
    !(opts.body instanceof FormData) &&
    !(opts.body instanceof Blob);

  if (isPlainObject) {
    if (!('Content-Type' in headers)) headers['Content-Type'] = 'application/json';
    if (headers['Content-Type']?.includes('application/json') && typeof opts.body !== 'string') {
      opts.body = JSON.stringify(opts.body);
    }
  } else if (typeof opts.body === 'string') {
    const looksJson = /^[\s]*[{\[]/.test(opts.body);
    if (looksJson && !('Content-Type' in headers)) {
      headers['Content-Type'] = 'application/json';
    }
  }

  opts.headers = headers;
  return fetch(api(path), opts);
};

/** Convenience JSON helper → { ok, status, data, text } */
export const apiJson = async (path, options = {}) => {
  const res = await apiFetch(path, options);
  const ct = String(res.headers.get('content-type') || '').toLowerCase();

  // Read the body once (res.json() would consume it and prevent us from seeing HTML fallback pages).
  const text = await res.clone().text().catch(() => '');
  let data = null;

  const trimmed = text.trim();
  const maybeJson =
    ct.includes('application/json') ||
    trimmed.startsWith('{') ||
    trimmed.startsWith('[');

  if (maybeJson && trimmed) {
    try { data = JSON.parse(trimmed); } catch { data = null; }
  }

  const looksHtml =
    ct.includes('text/html') ||
    /^<!doctype\s+html/i.test(trimmed) ||
    /<html[^>]*>/i.test(trimmed.slice(0, 400));

  // If we got HTML for an /api path, treat it as an error even if HTTP 200.
  const pathStr = String(path || '');
  const isApiPath = pathStr.startsWith('/api') || pathStr.includes('/api/');
  const ok = res.ok && !(looksHtml && isApiPath);

  return {
    ok,
    status: res.status,
    data,
    text: ok ? '' : (data ? '' : text),
    content_type: ct,
    url: res.url,
    is_html: looksHtml,
  };
};

/** Socket.IO (cookie-auth) */
const SOCKET_ORIGIN = get('VITE_SOCKET_ORIGIN', 'REACT_APP_SOCKET_ORIGIN') || API_BASE;
const SOCKET_PATH = `/${(get('VITE_SOCKET_PATH', 'REACT_APP_SOCKET_PATH') || '/socket.io')
  .replace(/^\/+/, '')
  .replace(/\/+$/, '')}`;

function createSocket() {
  const isDev =
    (V.MODE || process.env.NODE_ENV) === 'development' ||
    (hasWindow && (WLOC.port === '3000' || WLOC.hostname === 'localhost'));

  const s = io(SOCKET_ORIGIN, {
    path: SOCKET_PATH,
    auth: { token: (getToken() && getToken() !== 'session') ? getToken() : '' },
    withCredentials: true,                         // send session cookie
    transports: isDev ? ['polling', 'websocket']   // dev: start polling, then upgrade
                      : ['websocket', 'polling'],  // prod: prefer WS
    upgrade: true,
    reconnection: true,
    reconnectionAttempts: Infinity,
    reconnectionDelay: 500,
    reconnectionDelayMax: 3000,
  });

  if (DEBUG_SOCKET) {
    s.on('connect', () => console.log('[socket] connected', s.id));
    s.on('connect_error', (e) => console.warn('[socket] connect_error', e?.message || e));
    s.on('disconnect', (r) => console.log('[socket] disconnected', r));
  }
  return s;
}

const SOCKET_KEY = '__chitconnect_socket__';
export const socket =
  (hasWindow && window[SOCKET_KEY]) ||
  (hasWindow && (window[SOCKET_KEY] = createSocket())) ||
  createSocket();

export function refreshSocketAuth() {
  try {
    const token = getToken();
    socket.auth = { token: token && token !== 'session' ? token : '' };

    // Reconnect to ensure the new auth is used in the handshake
    if (socket.connected) socket.disconnect();
    socket.connect();
  } catch {}
}

/** ---------- User normalisation ---------- */
export function normalizeBool(v) {
  if (v === true || v === 1) return true;
  if (typeof v === 'string') {
    const s = v.trim().toLowerCase();
    return s === '1' || s === 'true' || s === 'yes' || s === 'y';
  }
  return false;
}

export function normalizeUserShape(raw) {
  // Accept either { user: {...} } or {...}
  const u = (raw && typeof raw === 'object' && 'user' in raw) ? raw.user : raw;
  if (!u || typeof u !== 'object') return null;
  const role = String(u.access_level ?? u.role ?? '').trim().toLowerCase();
  const approved = normalizeBool(u.approved_by_superadmin);
  return {
    ...u,
    role: role || undefined,
    access_level: role || u.access_level,
    approved_by_superadmin: approved,
  };
}


// Best-effort: trigger server-side auto-defer scan once per session (cooldown)
async function triggerAutoDeferScanOnce() {
  try {
    const key = 'autoDeferScanAt';
    const last = Number(sessionStorage.getItem(key) || 0);
    const now = Date.now();
    // 5-minute cooldown
    if (last && now - last < 5 * 60 * 1000) return;
    sessionStorage.setItem(key, String(now));
    // Fire-and-forget; ignore failure (eg. route not available yet)
    apiFetch('/api/bidding/auto-defer-scan', { method: 'POST' }).catch(() => {});
  } catch {
    // ignore
  }
}

/** Session helpers */
export async function fetchMe() {
  // Prefer /api/me; fall back to legacy /api/users/me
  let res = await apiFetch('/api/me');
  if (res.status === 404) res = await apiFetch('/api/users/me');
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  const raw = await res.json();
  const me = normalizeUserShape(raw);
  // Trigger auto-defer scan after we have a valid session
  triggerAutoDeferScanOnce();
  return me;
}

export async function refreshSessionUser() {
  const me = await fetchMe().catch(() => null);
  if (me) {
    try {
      localStorage.setItem('user', JSON.stringify(me));
      sessionStorage.setItem('user', JSON.stringify(me));
    } catch {}
  }
  return me;
}
